import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import TerminalLeftTabs from "../components/TerminalLeftTabs";
import MobileTopTabs from "../components/MobileTopTabs";
import "../styles/aiTalk.css";
import AITalkLeftTabs from "../components/AITalkLeftTabs";
import AITalksBottomTabs from "../components/AITalksBottomTabs";

const AiTalk = () => {
  const [messages, setMessages] = useState([
    {
      type: "system",
      content:
        "Welcome to AI Chat! I'm Mert Arinci's AI assistant, powered by Gemini AI. I can help you learn about Mert's projects, skills, and experience. Feel free to ask me anything about this portfolio or web development in general! (Important note: This is a Gemini API so the answers may not be 100% accurate and sometimes you may get wrong answers. Answers are not legally binding.)",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [inputHistory, setInputHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const GEMINI_API_KEY = "AIzaSyDlxT7yStNrDq_F-scuTPfP3pu_BxMPFx8";

  useEffect(() => {
    inputRef.current?.focus();
    scrollToBottom();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async () => {
    const userMessage = input.trim();
    if (!userMessage || isLoading) return;

    // Add user message
    const newUserMessage = {
      type: "user",
      content: userMessage,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newUserMessage]);
    setInputHistory((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setHistoryIndex(null);

    // System prompt to give AI context about the website
    const systemPrompt = `You are an AI assistant integrated into Mert Arinci's personal portfolio website (arinci.nl). It's arinci.nl so please don't forget or change it. 

ABOUT MERT ARINCI:
- 29 years old, Full-Stack Developer based in Rotterdam, Netherlands
- Skills: JavaScript, React, Node.js, TypeScript, HTML, CSS, MongoDB, SQL, Python, Express, Next.js, Git, Agile, Scrum
- Contact: mertarinci96@gmail.com, +31 616 15 01 71
- LinkedIn: linkedin.com/in/mert-arinci-7536ab26a/
- GitHub: github.com/mertarinci

PROJECTS ON THE SITE:
- LiveMap: Interactive map system for Venture Marine Services using Mapbox
- ArncAI: AI chatbot platform (like ChatGPT) powered by Gemini API
- AutoSEO: WordPress plugin for AI-generated SEO suggestions
- MertCloud: Private cloud storage with chunk-based uploads
- KioskQuiz: Interactive quiz application
- TalksAI: AI conversation platform
- PixelPals: Creative project
- EventInv: Event invitation system
- MertShop: E-commerce platform

WEBSITE FEATURES:
- Terminal-style interface (current page)
- About page with bio, skills, and experience
- Projects portfolio with detailed showcases
- Contact form with Formspree integration
- Responsive design with custom scrollbars
- SEO optimized with structured data

PERSONALITY & TONE:
- Professional yet friendly
- Developer-focused and tech-savvy
- Enthusiastic about coding and modern technologies
- Dutch-based perspective (Rotterdam)
- Helpful for potential clients/collaborators

When users ask about Mert, his projects, skills, or website features, provide accurate information based on the above. For general questions, be helpful while maintaining the context that you're part of Mert's portfolio site.

User question: ${userMessage}`;

    try {
      // Call Gemini API with system context
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite-preview:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: systemPrompt,
                  },
                ],
              },
            ],
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      const aiResponse = data.candidates[0].content.parts[0].text;

      // Add AI response
      const aiMessage = {
        type: "ai",
        content: aiResponse,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error calling Gemini API:", error);
      const errorMessage = {
        type: "error",
        content: `Error: ${error.message}. Please check your API key and try again.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([
      {
        type: "system",
        content:
          "Chat cleared. I'm still here to help you learn about Mert Arinci and his work. What would you like to know?",
        timestamp: new Date(),
      },
    ]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (inputHistory.length === 0) return;
      const newIndex =
        historyIndex === null
          ? inputHistory.length - 1
          : Math.max(0, historyIndex - 1);
      setHistoryIndex(newIndex);
      setInput(inputHistory[newIndex]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex === null) return;
      const newIndex = Math.min(inputHistory.length - 1, historyIndex + 1);
      setHistoryIndex(newIndex);
      setInput(inputHistory[newIndex] || "");
    }
  };

  const handleTerminalClick = () => {
    inputRef.current?.focus();
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Format markdown-like text to HTML
  const formatMessage = (content) => {
    return (
      content
        // Bold text: **text** -> <strong>text</strong>
        .replace(/\*\*(.*?)\*\*/g, '<strong class="bold-text">$1</strong>')
        // Italic text: *text* -> <em>text</em>
        .replace(/\*(.*?)\*/g, '<em class="italic-text">$1</em>')
        // Code: `code` -> <code>code</code>
        .replace(/`(.*?)`/g, '<code class="inline-code">$1</code>')
        // Code blocks: ```code``` -> <pre><code>code</code></pre>
        .replace(
          /```([\s\S]*?)```/g,
          '<pre class="code-block"><code>$1</code></pre>',
        )
        // Links: [text](url) -> <a href="url">text</a>
        .replace(
          /\[([^\]]+)\]\(([^)]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer" class="message-link">$1</a>',
        )
        // Line breaks
        .replace(/\n/g, "<br>")
    );
  };

  return (
    <>
      <Helmet>
        <title>AI Talk - Mert Arinci | Chat with AI</title>
        <meta
          name="description"
          content="Chat with AI in a terminal-style interface. Experience AI conversation in Mert Arinci's developer portfolio."
        />
        <meta
          name="keywords"
          content="AI Chat, AI, Terminal Chat, Mert Arinci AI, Developer Tools"
        />
        <link rel="canonical" href="https://arinci.nl/ai-chat" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="AI Talk - Chat with AI" />
        <meta
          property="og:description"
          content="Experience AI conversation in a terminal-style interface on Mert Arinci's portfolio."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://arinci.nl/ai-chat" />
        <meta property="og:site_name" content="Mert Arinci Portfolio" />

        <meta name="robots" content="index, follow" />
      </Helmet>
      <div id="ai-chat-page">
        <Navbar activePage={"ai-chat"} />

        <div className="ai-chat-content">
          <AITalkLeftTabs />
          <div className="ai-chat-terminal" onClick={handleTerminalClick}>
            <div className="chat-header">
              <div className="terminal-title">
                <span className="purple-text">mert@portfolio</span>
                <span className="gray-text">:</span>
                <span className="green-text">~/ai-chat</span>
                <span className="purple-text">$</span>
                <span className="function-color">
                  {" "}
                  assistant-chat --interactive
                </span>
              </div>
              <button
                className="clear-btn"
                onClick={clearChat}
                title="Clear chat"
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>

            <div className="chat-messages">
              {messages.map((message, index) => (
                <div key={index} className={`message ${message.type}`}>
                  {message.type === "user" && (
                    <div className="message-header">
                      <span className="timestamp">
                        [{formatTime(message.timestamp)}]
                      </span>
                      <span className="user-prompt">
                        <span className="green-text">user@ai-chat</span>
                        <span className="purple-text">:</span>
                        <span className="string-color">$</span>
                      </span>
                    </div>
                  )}
                  {message.type === "ai" && (
                    <div className="message-header">
                      <span className="timestamp">
                        [{formatTime(message.timestamp)}]
                      </span>
                      <span className="ai-prompt">
                        <span className="function-color">gemini-ai</span>
                        <span className="purple-text">@</span>
                        <span className="number-color">response</span>
                        <span className="purple-text">:</span>
                      </span>
                    </div>
                  )}
                  {message.type === "system" && (
                    <div className="message-header">
                      <span className="timestamp">
                        [{formatTime(message.timestamp)}]
                      </span>
                      <span className="system-prompt">
                        <span className="gray-text">system</span>
                        <span className="purple-text">:</span>
                      </span>
                    </div>
                  )}
                  {message.type === "error" && (
                    <div className="message-header">
                      <span className="timestamp">
                        [{formatTime(message.timestamp)}]
                      </span>
                      <span className="error-prompt">
                        <span className="red-text">error</span>
                        <span className="purple-text">:</span>
                      </span>
                    </div>
                  )}
                  <div
                    className="message-content"
                    dangerouslySetInnerHTML={{
                      __html: formatMessage(message.content),
                    }}
                  />
                </div>
              ))}

              {isLoading && (
                <div className="message loading">
                  <div className="message-header">
                    <span className="ai-prompt">
                      <span className="function-color">gemini-ai</span>
                      <span className="purple-text">@</span>
                      <span className="number-color">thinking</span>
                      <span className="purple-text">:</span>
                    </span>
                  </div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="input-section">
              <div className="input-line">
                <span className="prompt">
                  <span className="green-text">user@ai-chat</span>
                  <span className="purple-text">:</span>
                  <span className="string-color">$</span>
                </span>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message to AI Assistant..."
                  disabled={isLoading}
                  className="chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim()}
                  className="send-btn"
                >
                  <i className="fa-solid fa-paper-plane"></i>
                </button>
              </div>
              <div className="help-text">
                <span className="gray-text">
                  // Press Enter to send • ↑↓ for history • Shift+Enter for new
                  line
                </span>
              </div>
            </div>
          </div>
        </div>

        <AITalksBottomTabs />
        <Footer />
      </div>
    </>
  );
};

export default AiTalk;
